import { useCallback, useMemo, useRef, useState } from 'react'
import { PointerSensor, pointerWithin, useSensor, useSensors } from '@dnd-kit/core'
import type { Announcements, DragEndEvent, Modifier } from '@dnd-kit/core'
import type { PoolTarget } from '../../../Services/OrderWizard/pools.ts'
import type { Selection } from '../../../Services/OrderWizard/poolBoard.ts'
import { draggedKeys } from '../../../Services/OrderWizard/poolBoard.ts'
import { plural } from '../../../Util/plural.ts'

export type DropData = { target: PoolTarget; label: string }

/** A drop on the pool board: where it goes, or `undefined` outside the columns. */
export const dropTargetOf = (data: unknown): PoolTarget | undefined => (data as DropData | undefined)?.target

/* the drag starts on the whole column (one draggable per column, not per row), so the preview is placed at the pointer */
const atPointer: Modifier = ({ activatorEvent, activeNodeRect, overlayNodeRect, transform }) => {
  if (!activatorEvent || !activeNodeRect || !('clientX' in activatorEvent)) {
    return transform
  }
  const { clientX, clientY } = activatorEvent as globalThis.PointerEvent
  return {
    ...transform,
    x: transform.x + clientX - activeNodeRect.left + 8,
    y: transform.y + clientY - activeNodeRect.top - (overlayNodeRect?.height ?? 0) / 2
  }
}

export const DRAG_MODIFIERS = [atPointer]

const count = (n: number) => plural(n, 'item')

/**
 * The board's drag and drop: a drag from an item carries the selection when the item is in it, else just
 * the item, and a drop on a column moves it there. `onDropSelection` runs after a dragged selection lands.
 */
export const usePoolDrag = ({
  selection,
  onMove,
  onDropSelection
}: {
  selection: Selection
  onMove: (keys: string[], target: PoolTarget) => void
  onDropSelection: () => void
}) => {
  const [dragged, setDragged] = useState<string[]>([])
  const pressed = useRef<string | undefined>(undefined)
  const carried = useRef(0)
  const draggedSet = useMemo(() => new Set(dragged), [dragged])
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))
  const onPress = useCallback((key: string) => (pressed.current = key), [])

  const onDragStart = () => {
    const keys = pressed.current ? draggedKeys(selection, pressed.current) : []
    carried.current = keys.length
    setDragged(keys)
  }
  const onDragEnd = ({ over }: DragEndEvent) => {
    const target = dropTargetOf(over?.data.current)
    if (target !== undefined && dragged.length > 0) {
      onMove(dragged, target)
      if (pressed.current && selection.keys.has(pressed.current)) {
        onDropSelection()
      }
    }
    setDragged([])
  }
  const announcements: Announcements = {
    onDragStart: () => `Picked up ${count(carried.current)}.`,
    onDragOver: ({ over }) => (over ? `Over ${(over.data.current as DropData).label}.` : 'Not over a pool.'),
    onDragEnd: ({ over }) =>
      over ? `Moved ${count(carried.current)} to ${(over.data.current as DropData).label}.` : 'Dropped outside the pools; nothing moved.',
    onDragCancel: () => 'Drag cancelled; nothing moved.'
  }

  return {
    dragged,
    draggedSet,
    onPress,
    /** The props for the board's `DndContext`. */
    context: {
      sensors,
      collisionDetection: pointerWithin,
      onDragStart,
      onDragEnd,
      onDragCancel: () => setDragged([]),
      accessibility: {
        announcements,
        screenReaderInstructions: { draggable: 'Select items with their checkboxes and move them with Move to in the selection bar.' }
      }
    }
  }
}
